import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../store';
import { MENU_MS, useDismiss } from '../motion';
import { WINDOW_CHOICES, WINDOW_LABEL, type InsightsScope } from '../insights-scope';
import Arrange from './Arrange';
import Icon from './Icon';
import type { Layout } from '../insights-layout';

/**
 * WHAT INSIGHTS IS LOOKING AT, AND THE HEADLINE OF IT — the strip across the top.
 *
 * It replaces the four tiles. The tiles said open, blocked, complete a second
 * time in big numbers; here those facts are a glance line beside the controls
 * that decide what they count — the board or all of them, and how far back. One
 * strip answers "what am I looking at" and "how is it, in five numbers" without
 * spending a quarter of the screen on it.
 *
 * The board picker is the same switch as the sidebar (setProject) — choosing a
 * board here changes the board everywhere, which is the honest thing: there is
 * one board open at a time, and this says which. Across all boards there is no
 * one board to pick, so the picker steps aside.
 */
export interface Glance {
  open: number;
  pct: number;
  inFlight: number;
  stuck: number;
  blocked: number;
}

/** A small dropdown that closes on a click away or Escape — the app standard. */
function Menu({ label, title, children }: { label: ReactNode; title: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const { leaving, dismiss } = useDismiss(useCallback(() => setOpen(false), []), MENU_MS);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) dismiss(); };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && dismiss();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open, dismiss]);
  return (
    <div className="ins-menu-wrap" ref={wrap}>
      <button className="ins-menu-trigger" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu" title={title}>
        {label}
        <Icon name="chevron" size={16} className="ins-menu-caret" />
      </button>
      {open && (
        <div className={`ins-menu ${leaving ? 'is-leaving' : ''}`} role="menu">
          {children(dismiss)}
        </div>
      )}
    </div>
  );
}

export default function InsightsStrip({
  glance, layout, onLayoutChange, arranging, onArrangingChange,
}: {
  glance: Glance;
  layout: Layout;
  onLayoutChange: (l: Layout) => void;
  arranging: boolean;
  onArrangingChange: (on: boolean) => void;
}) {
  const scope = useApp((s) => s.insightsScope);
  const windowDays = useApp((s) => s.insightsWindow);
  const setScope = useApp((s) => s.setInsightsScope);
  const setWindow = useApp((s) => s.setInsightsWindow);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const setProject = useApp((s) => s.setProject);
  const drillTo = useApp((s) => s.drillTo);
  const meta = useApp((s) => s.meta);

  /* Arrange is a MODE now, not a menu. Pressing Arrange turns the grid's grips,
     pins and resize handles on and leaves them on until "Done arranging" — a click
     on the grid IS the point of the mode, so it must not end it. The old auto-exit
     on an outside click was removed with the coupling it belonged to.

     "Show panels" is a separate popover in the arrange bar. It behaves like the
     other strip menus: opens on its own button, closes on a click away (scoped to
     the `.ins-arrange` wrapper, so the checkboxes and the reset stay "inside") or on
     Escape (handled in Arrange via useEscapeToClose). Only the popover closes;
     arrange mode stays on. */
  const [showPanels, setShowPanels] = useState(false);
  const arrangeWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showPanels) return;
    const away = (e: MouseEvent) => {
      if (!arrangeWrap.current?.contains(e.target as Node)) setShowPanels(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [showPanels]);
  /* Leaving arrange mode takes the popover with it, so re-entering never reopens
     the checklist over the grid — the very coupling this fix removed. */
  useEffect(() => { if (!arranging) setShowPanels(false); }, [arranging]);

  const current = projects.find((p) => p.id === projectId);
  const boardName = current?.name ?? meta.name;
  const boardKey = current?.key ?? meta.key;

  const tab = (s: InsightsScope, text: string) => (
    <button
      role="tab"
      aria-selected={scope === s}
      className={`seg-btn ${scope === s ? 'active' : ''}`}
      onClick={() => setScope(s)}
    >
      {text}
    </button>
  );

  return (
    <div className="ins-strip">
      <div className="ins-controls">
        <div className="seg ins-scope" role="tablist">
          {tab('board', 'This board')}
          {tab('all', 'All my boards')}
        </div>

        {/* One board open at a time — the picker is the sidebar's switch. With a
            single board it is a plain label, because a menu onto a list of one
            teaches people not to press things. Across boards there is nothing to
            pick. */}
        {scope === 'board' && (
          projects.length > 1 ? (
            <Menu
              title="Switch board"
              label={<><span className="ins-badge">{boardKey}</span><span className="ins-board-name">{boardName}</span></>}
            >
              {(close) => (
                <>
                  <div className="ins-menu-label">Boards</div>
                  {projects.map((p) => (
                    <button
                      key={p.id}
                      role="menuitem"
                      className={`ins-menu-item ${p.id === projectId ? 'is-current' : ''}`}
                      onClick={() => { setProject(p.id); close(); }}
                    >
                      <span className="ins-badge">{p.key}</span>
                      <span className="ins-menu-item-name">{p.name}</span>
                      {p.id === projectId && <Icon name="check" size={13} />}
                    </button>
                  ))}
                </>
              )}
            </Menu>
          ) : (
            <span className="ins-board-chip"><span className="ins-badge">{boardKey}</span><span className="ins-board-name">{boardName}</span></span>
          )
        )}

        {/* Across all boards this screen is flow only — the RAG, schedule and
            budget for every project live on the Portfolio, so it points there
            rather than pretending to answer them here. The note says WHY it is
            flow only, so a reader does not mistake the missing health for
            unbuilt (workflow witness, 24 Sep); the link uses drillTo so the
            Portfolio arrives with a "← Back to Insights" trail. */}
        {scope === 'all' && (
          <span className="ins-allnote">
            <span className="ins-flowonly">Flow only across boards.</span>
            <button type="button" className="link ins-portfolio" onClick={() => drillTo('portfolio')}>
              Status, schedule &amp; budget on the Portfolio <Icon name="chevron" size={14} />
            </button>
          </span>
        )}

        <Menu title="Change the time window" label={<span className="ins-window">{WINDOW_LABEL[windowDays]}</span>}>
          {(close) => (
            <>
              <div className="ins-menu-label">Window</div>
              {WINDOW_CHOICES.map((w) => (
                <button
                  key={w}
                  role="menuitem"
                  className={`ins-menu-item ${w === windowDays ? 'is-current' : ''}`}
                  onClick={() => { setWindow(w); close(); }}
                >
                  <span className="ins-menu-item-name">{WINDOW_LABEL[w]}</span>
                  {w === windowDays && <Icon name="check" size={13} />}
                </button>
              ))}
            </>
          )}
        </Menu>
      </div>

      <div className="ins-right">
        <div className="ins-glance" aria-label="Summary">
          <span className="ins-stat"><b>{glance.open}</b> open</span>
          {/* (F5) Names its basis — this is cards-done ÷ counted, a card count like the
              figures beside it, NOT the weighted "% complete" the Portfolio/EVM read (typed %
              + DoD). Same bare word on both screens read as a 3× disagreement; "of cards done"
              tells them apart. */}
          <span className="ins-stat"><b>{glance.pct}%</b> of cards done</span>
          <span className="ins-stat"><b>{glance.inFlight}</b> in flight</span>
          <span className="ins-stat"><b className="ins-warn">{glance.stuck}</b> stuck</span>
          <span className="ins-stat"><b className="ins-warn">{glance.blocked}</b> blocked</span>
        </div>
        <div className="ins-arrange" ref={arrangeWrap}>
          <button
            className={`btn btn-small ins-arrange-btn ${arranging ? 'is-on' : ''}`}
            aria-pressed={arranging}
            onClick={() => onArrangingChange(!arranging)}
          >
            <Icon name="grid" size={15} />
            {arranging ? 'Done arranging' : 'Arrange'}
          </button>
          {arranging && (
            <button
              className="btn btn-small ins-panels-btn"
              aria-haspopup="dialog"
              aria-expanded={showPanels}
              onClick={() => setShowPanels((v) => !v)}
            >
              <Icon name="eye" size={15} />
              Show panels
            </button>
          )}
          {arranging && showPanels && (
            <Arrange layout={layout} onChange={onLayoutChange} onClose={() => setShowPanels(false)} />
          )}
        </div>
      </div>
    </div>
  );
}
